import { useState } from 'react';
import { Box, Chip, InputAdornment, Stack, TextField } from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import { useTaxonomyOptions } from '../../hooks/useTaxonomyOptions';
import { useDescriptorUpsert } from '../../hooks/useDescriptorUpsert';
import {
  useAddDescriptor,
  useDeleteDescriptor,
  type EntityDescriptorDto,
} from '../../api/queries/entities';
import type { CharacterFieldConfig } from './characterFieldConfig';
import type { SelectOption } from '../common/FilterableSelect';

// ---------------------------------------------------------------------------
// Single-value combobox
// ---------------------------------------------------------------------------

function SingleCombobox({
  field,
  entityId,
  descriptors,
}: {
  field: CharacterFieldConfig;
  entityId: string;
  descriptors: EntityDescriptorDto[];
}) {
  const { getValue, upsert } = useDescriptorUpsert(entityId, descriptors);
  const [search, setSearch] = useState('');
  const taxonomy = useTaxonomyOptions(field.taxonomyCategoryKey, search);
  const currentValue = getValue(field.category);

  const options: SelectOption[] = field.taxonomyCategoryKey
    ? taxonomy.options
    : (field.staticOptions ?? []).map((o) => ({ value: o, label: o }));

  return (
    <FilterableSelect
      options={options}
      value={currentValue}
      onChange={(val) => void upsert(field.category, val)}
      label={field.label}
      freeSolo
      onInputChange={field.taxonomyCategoryKey ? setSearch : undefined}
      loading={field.taxonomyCategoryKey ? taxonomy.isLoading : false}
      allowEmpty={false}
      size="small"
    />
  );
}

// ---------------------------------------------------------------------------
// Integer field
// ---------------------------------------------------------------------------

function IntegerField({
  field,
  entityId,
  descriptors,
}: {
  field: CharacterFieldConfig;
  entityId: string;
  descriptors: EntityDescriptorDto[];
}) {
  const { getValue, upsert } = useDescriptorUpsert(entityId, descriptors);
  const currentValue = getValue(field.category);
  const [draft, setDraft] = useState<string | null>(null);

  // Use draft while editing; fall back to stored value
  const displayValue = draft ?? currentValue;

  return (
    <TextField
      label={field.label}
      type="number"
      size="small"
      fullWidth
      value={displayValue}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== null) {
          void upsert(field.category, draft);
          setDraft(null);
        }
      }}
      slotProps={{
        input: {
          endAdornment: field.unit ? (
            <InputAdornment position="end">{field.unit}</InputAdornment>
          ) : undefined,
        },
        htmlInput: { min: 0 },
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Multi-value combobox (chips + add)
// ---------------------------------------------------------------------------

function MultiValueField({
  field,
  entityId,
  descriptors,
}: {
  field: CharacterFieldConfig;
  entityId: string;
  descriptors: EntityDescriptorDto[];
}) {
  const add = useAddDescriptor(entityId);
  const remove = useDeleteDescriptor(entityId);
  const [search, setSearch] = useState('');
  const [inputKey, setInputKey] = useState(0);
  const taxonomy = useTaxonomyOptions(field.taxonomyCategoryKey, search);

  const items = descriptors.filter(
    (d) => d.category.toLowerCase() === field.category.toLowerCase(),
  );
  const existingValues = new Set(items.map((d) => d.content.toLowerCase()));

  const allOptions: SelectOption[] = field.taxonomyCategoryKey
    ? taxonomy.options
    : (field.staticOptions ?? []).map((o) => ({ value: o, label: o }));

  const availableOptions = allOptions.filter(
    (o) => !existingValues.has(o.value.toLowerCase()),
  );

  const handleAdd = (val: string) => {
    if (!val.trim()) return;
    if (existingValues.has(val.trim().toLowerCase())) return;
    void add.mutateAsync({ category: field.category, content: val.trim() });
    setInputKey((k) => k + 1); // force remount to clear input
    setSearch('');
  };

  return (
    <Box>
      {items.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 0.5 }}>
          {items.map((d) => (
            <Chip
              key={d.id}
              label={d.content}
              size="small"
              onDelete={() => remove.mutate(d.id)}
            />
          ))}
        </Stack>
      )}
      <FilterableSelect
        key={inputKey}
        options={availableOptions}
        value=""
        onChange={(val) => {
          handleAdd(val);
        }}
        label={
          items.length === 0
            ? field.label
            : `Add ${field.label.toLowerCase()}...`
        }
        freeSolo
        onInputChange={field.taxonomyCategoryKey ? setSearch : undefined}
        loading={field.taxonomyCategoryKey ? taxonomy.isLoading : false}
        allowEmpty={false}
        size="small"
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main dispatcher
// ---------------------------------------------------------------------------

interface StructuredFieldInputProps {
  field: CharacterFieldConfig;
  entityId: string;
  descriptors: EntityDescriptorDto[];
}

export function StructuredFieldInput({
  field,
  entityId,
  descriptors,
}: StructuredFieldInputProps) {
  if (field.multiValue) {
    return (
      <MultiValueField
        field={field}
        entityId={entityId}
        descriptors={descriptors}
      />
    );
  }

  if (field.fieldType === 'integer') {
    return (
      <IntegerField
        field={field}
        entityId={entityId}
        descriptors={descriptors}
      />
    );
  }

  return (
    <SingleCombobox
      field={field}
      entityId={entityId}
      descriptors={descriptors}
    />
  );
}
